<template>
  <DModal :show-close="false" :isBgOverlay="true" :appendToBody="true" ariaLabel="Omlagt dag" @close="$emit('close')">
    <div class="sdc text-dark" slot="insertComponent">
      <div class="sdc__topbar">
        <div class="sdc__topbar__main">
          <div class="sdc__field sdc__field--title">
            <label class="d-label">Navn</label>
            <input type="text" v-model="form.title" placeholder="F.eks. Motionsdag"
                   class="d-input" ref="input_title" v-focus>
          </div>

          <div class="sdc__field sdc__field--classes">
            <div class="sdc__label-row">
              <label class="d-label">Klasser</label>
              <button type="button" class="sdc__link" @click="toggleAllClasses">
                {{ allClassesSelected ? 'Ryd' : 'Vælg alle' }}
              </button>
            </div>
            <DSelectCals
              :items="klassCalendars"
              :itemsSelected="selectedClasses"
              placeholder="Vælg klasser"
              @update="onClassesUpdate">
            </DSelectCals>
          </div>

          <div class="sdc__field sdc__field--toggle">
            <label class="d-label">Reducer på fagtimer i klassen</label>
            <div
              class="sdc__toggler"
              :class="{'sdc__toggler--active': form.reduct}"
              @click.stop="form.reduct = !form.reduct">
              <AppToggle :allowChanges="false" :propOn="form.reduct"/>
              <span class="sdc__toggler__text">{{ form.reduct ? 'Ja' : 'Nej' }}</span>
            </div>
          </div>
        </div>

        <div class="font-large icon__bg-circle sdc__close" @click="$emit('close')">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <div class="sdc__body">
        <Loader v-if="isLoadingExisting"></Loader>
        <template v-else>
          <div class="sdc__calendar-head">
            <span class="sdc__period" v-if="rangeStart">{{ rangeLabel }}</span>
            <span class="sdc__period sdc__period--empty" v-else>Klik på en dag for at vælge fra- og til-dato</span>
            <button type="button" class="sdc__link" v-if="rangeStart" @click="clearRange">Ryd</button>
          </div>

          <p v-if="hasTriedSave && selectedClasses.length === 0" class="sdc__warning">Vælg mindst én klasse</p>
          <p v-if="hasTriedSave && !rangeStart" class="sdc__warning">Vælg en periode i kalenderen</p>

          <div class="sdc__calendar">
            <div v-for="month in months" :key="month.key" class="sdc__month-row">
              <div class="sdc__month-name">{{ month.label }}</div>
              <div class="sdc__days">
                <div v-for="day in month.days" :key="day.date"
                     class="sdc__day"
                     :class="{
                       'sdc__day--out': !day.selectable,
                       'sdc__day--weekend': day.isWeekend,
                       'sdc__day--in-range': isInRange(day),
                       'sdc__day--start': day.date === rangeStart,
                       'sdc__day--end': day.date === effectiveEnd
                     }"
                     @click="selectDay(day)">
                  {{ day.label }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <div class="sdc__footer">
        <button class="do-bttn" @click="$emit('close')" :disabled="isSaving">Annuller</button>
        <button
          class="do-bttn do-bttn--submit"
          :class="{
            'do-bttn--submit--disabled': !isValid || isSaving,
            'do-bttn--submit--loading': isSaving
          }"
          @click="save">Gem</button>
      </div>
    </div>
  </DModal>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import DModal from '../common/DModal'
import Loader from '../common/Loader'
import DSelectCals from '../DSelect/DSelectCals'
import AppToggle from '../common/AppToggle'

export default {
  name: 'special-day-calendar-modal',
  components: {DModal, Loader, DSelectCals, AppToggle},
  props: {
    schoolId: {
      default: null
    },
    schoolYearId: {
      default: null
    },
    specialDayId: {
      default: null
    },
    klassCalendars: {
      type: Array,
      default: () => []
    },
    // Skoleårets student-periode — afgrænser kalenderen.
    schoolYearStart: {
      default: null
    },
    schoolYearEnd: {
      default: null
    }
  },
  directives: {
    focus: {
      inserted (el) {
        setTimeout(() => el.focus(), 150)
      }
    }
  },
  data () {
    return {
      isSaving: false,
      isLoadingExisting: false,
      hasTriedSave: false,
      form: {
        title: '',
        reduct: true
      },
      rangeStart: null,
      rangeEnd: null,
      selectedClasses: []
    }
  },
  computed: {
    calStart () {
      return this.schoolYearStart ? moment(this.schoolYearStart) : moment().startOf('month')
    },
    calEnd () {
      return this.schoolYearEnd ? moment(this.schoolYearEnd) : moment().add(11, 'month').endOf('month')
    },
    months () {
      const months = []
      const cursor = this.calStart.clone().startOf('month')
      const last = this.calEnd.clone().endOf('month')
      while (cursor.isSameOrBefore(last)) {
        months.push(this.buildMonth(cursor.clone()))
        cursor.add(1, 'month')
      }
      return months
    },
    effectiveEnd () {
      return this.rangeEnd || this.rangeStart
    },
    allClassesSelected () {
      return this.klassCalendars.length > 0 && this.selectedClasses.length === this.klassCalendars.length
    },
    rangeLabel () {
      if (!this.rangeStart) {
        return ''
      }
      const start = moment(this.rangeStart)
      const end = moment(this.effectiveEnd)
      if (start.isSame(end, 'day')) {
        return start.format('dddd D. MMMM YYYY')
      }
      return start.format('D. MMM') + ' – ' + end.format('D. MMM YYYY')
    },
    isValid () {
      return this.form.title.trim().length > 0 &&
        !!this.rangeStart &&
        this.selectedClasses.length > 0
    }
  },
  mounted () {
    if (this.specialDayId) {
      this.loadExisting()
    }
  },
  methods: {
    buildMonth (monthStart) {
      const monthEnd = monthStart.clone().endOf('month')
      const days = []
      const day = monthStart.clone()
      while (day.isSameOrBefore(monthEnd, 'day')) {
        days.push({
          date: day.format('YYYY-MM-DD'),
          label: day.date(),
          isWeekend: day.isoWeekday() >= 6,
          selectable: day.isSameOrAfter(this.calStart, 'day') && day.isSameOrBefore(this.calEnd, 'day')
        })
        day.add(1, 'day')
      }
      return {key: monthStart.format('YYYY-MM'), label: monthStart.format('MMMM'), days: days}
    },
    isInRange (day) {
      if (!this.rangeStart || !this.effectiveEnd) {
        return false
      }
      return day.date > this.rangeStart && day.date < this.effectiveEnd
    },
    selectDay (day) {
      if (!day.selectable) {
        return
      }
      const d = day.date
      if (!this.rangeStart || this.rangeEnd) {
        this.rangeStart = d
        this.rangeEnd = null
      } else if (d < this.rangeStart) {
        this.rangeEnd = this.rangeStart
        this.rangeStart = d
      } else {
        this.rangeEnd = d
      }
    },
    clearRange () {
      this.rangeStart = null
      this.rangeEnd = null
    },
    onClassesUpdate (classes) {
      this.selectedClasses = classes.slice()
    },
    toggleAllClasses () {
      this.selectedClasses = this.allClassesSelected ? [] : this.klassCalendars.slice()
    },
    loadExisting () {
      this.isLoadingExisting = true
      docendo.specialDaysShow(this.schoolId, this.schoolYearId, this.specialDayId, data => {
        this.form = {
          title: data.title || '',
          reduct: data.reduct !== false
        }
        this.rangeStart = data.start_date || null
        this.rangeEnd = data.end_date || null
        const ids = data.calendar_ids || []
        this.selectedClasses = this.klassCalendars.filter(cal => ids.indexOf(cal.id) > -1)
        this.isLoadingExisting = false
      }, () => {
        this.isLoadingExisting = false
        eventbus.toast = {text: 'Noget gik galt. Prøv igen.', success: false}
      })
    },
    buildAttrs () {
      return {
        title: this.form.title.trim(),
        start_date: this.rangeStart,
        end_date: this.effectiveEnd,
        reduct: this.form.reduct,
        calendar_ids: this.selectedClasses.map(cal => cal.id)
      }
    },
    save () {
      this.hasTriedSave = true
      if (!this.isValid || this.isSaving) {
        return
      }
      this.isSaving = true
      const attrs = this.buildAttrs()
      const onSaved = data => {
        if (data && data.status === 'error') {
          this.onError()
          return
        }
        this.isSaving = false
        eventbus.toast = {text: 'Gemt', success: true}
        this.$emit('saved')
      }
      if (this.specialDayId) {
        docendo.specialDaysUpdate(this.schoolId, this.schoolYearId, this.specialDayId, attrs, onSaved, this.onError)
      } else {
        docendo.specialDaysCreate(this.schoolId, this.schoolYearId, attrs, onSaved, this.onError)
      }
    },
    onError () {
      this.isSaving = false
      eventbus.toast = {text: 'Noget gik galt. Prøv igen.', success: false}
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.sdc {
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
}

.sdc__topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  background: #fff;
  padding: 1rem 1.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
  position: relative;
  z-index: 2;
}

.sdc__topbar__main {
  display: flex;
  align-items: flex-end;
  gap: 1.5rem;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}

.sdc__field {
  display: flex;
  flex-direction: column;
}

.sdc__field--title {
  flex: 1 1 260px;
  min-width: 200px;
}

.sdc__field--classes {
  flex: 1 1 320px;
  min-width: 240px;
}

.sdc__field--toggle {
  flex: 0 0 auto;
}

.sdc__field .d-label {
  margin-bottom: 0.25rem;
}

.sdc__field .d-input {
  height: 38px;
  width: 100%;
}

.sdc__label-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.sdc__link {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 10px;
  color: var(--blue);
  text-decoration: underline;
}

.sdc__link:hover {
  color: var(--dark-blue);
}

.sdc__toggler {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 38px;
  padding: 0 0.75rem;
  background: #fff;
  border: 1px solid #acb6be;
  border-radius: 4px;
  cursor: pointer;
}

.sdc__toggler--active {
  border-color: var(--blue);
}

.sdc__toggler__text {
  font-size: 13px;
}

.sdc__close {
  font-size: 1.2rem;
  cursor: pointer;
  flex-shrink: 0;
}

.sdc__body {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

.sdc__calendar-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.sdc__period {
  font-size: 15px;
  font-weight: bold;
  color: var(--dark-blue);
  text-transform: capitalize;
}

.sdc__period--empty {
  font-weight: normal;
  color: var(--grey);
  text-transform: none;
}

.sdc__warning {
  margin: 0 0 0.75rem 0;
  color: var(--danger);
  font-size: 13px;
}

/* Kalender i samme stil som vacations-days-selector__year-calendar (DirectDaySelector). */
.sdc__calendar {
  display: flex;
  flex-direction: column;
}

.sdc__month-row {
  display: flex;
  margin-bottom: 1rem;
}

.sdc__month-name {
  width: 90px;
  flex-shrink: 0;
  font-weight: bold;
  text-transform: capitalize;
  padding-top: 8px;
}

.sdc__days {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
}

.sdc__day {
  cursor: pointer;
  text-align: center;
  user-select: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  line-height: 36px;
  color: var(--font-blue);
  border: 1px solid var(--bg-grey);
  transition: transform 0.2s;
}

.sdc__day--weekend {
  background-color: var(--bg-button);
}

.sdc__day--out {
  color: var(--border-color);
  cursor: default;
}

.sdc__day:not(.sdc__day--out):hover {
  border: 1px solid var(--blue);
}

.sdc__day:not(.sdc__day--out):active {
  transform: scale(0.95);
}

.sdc__day--in-range {
  background-color: #dcebfb;
  color: var(--dark-blue);
}

.sdc__day--start,
.sdc__day--end {
  background-color: var(--blue);
  color: #fff;
  font-weight: bold;
}

.sdc__footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: #fff;
  border-top: 1px solid var(--ligthGrey);
}

.sdc >>> .d-select__input-look {
  min-height: 38px;
  box-shadow: none;
  border: 1px solid #acb6be;
}

.sdc >>> .d-select__input-look.active {
  border-color: var(--blue);
}

.sdc >>> .d-select__header__search,
.sdc >>> .d-select__header__search.show.input-active {
  box-shadow: none;
}
</style>



// WEBPACK FOOTER //
// SpecialDayCalendarModal.vue?6f615002